import React, { useState } from 'react';
import { View, Pressable } from 'react-native';

import Caption from '~/client/components/Caption';
import ErrorMessage from '~/client/components/Error';
import Text from '~/client/components/Text';
import { useMusicKit } from '~/client/utils/MusicKitContext';
import { useLoggedIn } from '~/client/utils/LoggedInContext';
import { fetchFromServer } from '~/client/utils/fetchJSONFromServer';
import isWeb from '~/client/utils/isWeb';
import logger from '~/client/utils/logger';

const APPLE_MUSIC_API_BASE = 'https://api.music.apple.com/v1';
const LIBRARY_PAGE_SIZE = 100;
const MAX_LIBRARY_PAGES = 50; // safety cap: up to 5,000 library songs
const RATINGS_BATCH_SIZE = 25; // Apple's batch-get limit for `ids`

interface LibrarySong {
  attributes?: {
    playParams?: {
      catalogId?: string;
    };
  };
}

interface SongRating {
  id: string;
  attributes?: {
    value?: number;
  };
}

function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

export default function ImportAppleMusicFavorites({
  active,
}: {
  active: boolean;
}) {
  const musicKit = useMusicKit();
  const loggedInUser = useLoggedIn();

  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importedCount, setImportedCount] = useState<number | null>(null);

  if (!active || !isWeb()) {
    return null;
  }

  const authenticatedFetch = async (path: string, music: MusicKit.MusicKitInstance) => {
    const response = await fetch(`${APPLE_MUSIC_API_BASE}${path}`, {
      headers: {
        Authorization: `Bearer ${music.developerToken}`,
        'Music-User-Token': music.musicUserToken,
      },
    });

    if (!response.ok) {
      throw new Error(`Apple Music API request failed: ${path} (${response.status})`);
    }

    return response.json();
  };

  const fetchLibraryCatalogIds = async (
    music: MusicKit.MusicKitInstance,
  ): Promise<string[]> => {
    const catalogIds: string[] = [];

    for (let page = 0; page < MAX_LIBRARY_PAGES; page++) {
      const offset = page * LIBRARY_PAGE_SIZE;
      setStatus(`Reading your library (${catalogIds.length} songs seen)...`);

      const data: { data: LibrarySong[] } = await authenticatedFetch(
        `/me/library/songs?limit=${LIBRARY_PAGE_SIZE}&offset=${offset}`,
        music,
      );

      for (const librarySong of data.data) {
        const catalogId = librarySong.attributes?.playParams?.catalogId;
        if (catalogId) {
          catalogIds.push(catalogId);
        }
      }

      if (data.data.length < LIBRARY_PAGE_SIZE) {
        break;
      }
    }

    return catalogIds;
  };

  const fetchLovedCatalogIds = async (
    music: MusicKit.MusicKitInstance,
    catalogIds: string[],
  ): Promise<string[]> => {
    const loved: string[] = [];

    for (const batch of chunk(catalogIds, RATINGS_BATCH_SIZE)) {
      setStatus(
        `Checking which of your ${catalogIds.length} library songs are loved...`,
      );

      const data: { data: SongRating[] } = await authenticatedFetch(
        `/me/ratings/songs?ids=${batch.join(',')}`,
        music,
      );

      for (const rating of data.data) {
        if (rating.attributes?.value === 1) {
          loved.push(rating.id);
        }
      }
    }

    return loved;
  };

  const handleImport = async () => {
    if (!loggedInUser?.email) {
      window.alert('Please log in to import favorites.');
      return;
    }

    if (!musicKit.isAvailable) {
      setError('Apple Music is still loading. Please try again in a moment.');
      return;
    }

    setIsImporting(true);
    setError(null);
    setImportedCount(null);
    try {
      const music = MusicKit.getInstance();

      if (!music.isAuthorized) {
        setStatus('Connecting to Apple Music...');
        await music.authorize();
      }

      const catalogIds = await fetchLibraryCatalogIds(music);
      const lovedCatalogIds = await fetchLovedCatalogIds(music, catalogIds);

      let imported = 0;
      for (const catalogId of lovedCatalogIds) {
        setStatus(`Importing ${imported + 1} of ${lovedCatalogIds.length}...`);

        const response = await fetchFromServer(
          `/song/fromAppleCatalogId/${encodeURIComponent(catalogId)}/favorite`,
          { method: 'POST' },
        );

        if (response.ok) {
          imported++;
        } else {
          logger.warn('Failed to import one favorite', { catalogId });
        }
      }

      setImportedCount(imported);
      setStatus(null);
    } catch (e) {
      logger.error('Failed to import Apple Music favorites', { error: e });
      setError('Could not import your favorites. Please try again.');
      setStatus(null);
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <View className="my-4 w-[93%] md:w-full">
      <ErrorMessage errorText={error} />

      <Pressable
        disabled={isImporting}
        onPress={handleImport}
        style={{
          opacity: isImporting ? 0.5 : 1,
          // @ts-expect-error `cursor` is a web-only style property
          cursor: isImporting ? 'default' : 'pointer',
        }}
      >
        <Text style={{ fontWeight: 'bold' }}>
          {isImporting ? 'Importing...' : 'Import Loved Songs'}
        </Text>
      </Pressable>
      <Caption>
        Signs you in with Apple Music and imports every song you've loved
        into your Streamsense favorites.
      </Caption>

      {status ? <Text>{status}</Text> : null}
      {importedCount !== null ? (
        <Text>
          {importedCount > 0
            ? `Imported ${importedCount} song${importedCount === 1 ? '' : 's'}.`
            : "Didn't find any loved songs in your library."}
        </Text>
      ) : null}
    </View>
  );
}
